import { apiFetch } from "../../api/apiFetch";

export const DAILY_SCORE_KEY = "gamification-daily-score";
export const SCORE_EVENT = "gamification-score-updated";

const FLUSH_INTERVAL_MS = 60_000;

interface StoredScore {
    uid: number;
    date: string;
    score: number;
}

let pendingUid: number | null = null;
let pendingSeconds = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushInFlight = false;

const todayKey = (): string => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const currentUid = (): number | null => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const uid = Number(parsed?.uid);
        return Number.isFinite(uid) && uid > 0 ? uid : null;
    } catch {
        return null;
    }
};

const readStored = (): StoredScore | null => {
    try {
        const raw = window.localStorage.getItem(DAILY_SCORE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredScore;
        if (
            typeof parsed?.uid !== "number" ||
            typeof parsed?.date !== "string" ||
            typeof parsed?.score !== "number"
        ) return null;
        return parsed;
    } catch {
        return null;
    }
};

const writeStored = (data: StoredScore) => {
    try {
        window.localStorage.setItem(DAILY_SCORE_KEY, JSON.stringify(data));
    } catch {
        // localStorage may not be available
    }
};

const dispatch = (total: number) => {
    try {
        window.dispatchEvent(new CustomEvent(SCORE_EVENT, { detail: total }));
    } catch {
        // noop
    }
};

/**
 * Today's banked score, or null when this device has none for this user.
 *
 * The null matters to exactly one caller: the milestone watcher needs to tell
 * "no baseline yet" from "a genuine zero". Seeding a baseline of 0 on a device
 * that simply hasn't loaded the day's total yet makes the login refresh look
 * like the operator just earned everything at once.
 */
export const getStoredScore = (): number | null => {
    const stored = readStored();
    if (!stored) return null;
    const uid = currentUid();
    if (uid === null || stored.uid !== uid) return null;
    if (stored.date !== todayKey()) return null;
    return stored.score > 0 ? stored.score : 0;
};

export const getSessionScore = (): number => getStoredScore() ?? 0;

const sendBatch = async (
    uid: number,
    seconds: number,
    keepalive: boolean,
): Promise<number | null> => {
    try {
        const res = await apiFetch(`/points/add`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ uid, seconds }),
            keepalive,
        });
        if (!res.ok) return null;
        const data = await res.json();
        const score = Number(data?.score);
        return Number.isFinite(score) && score >= 0 ? score : null;
    } catch {
        return null;
    }
};

const scheduleFlush = () => {
    if (flushTimer !== null) return;
    flushTimer = setTimeout(() => {
        flushTimer = null;
        void flushPending(false);
    }, FLUSH_INTERVAL_MS);
};

// Ship the accumulated delta to the server. `keepalive` lets it survive a
// page unload (used from pagehide/visibilitychange so points aren't lost
// when the user closes the tab or the app is backgrounded).
const flushPending = async (keepalive: boolean): Promise<void> => {
    if (flushInFlight) return;
    if (pendingSeconds <= 0 || pendingUid === null) return;

    const uid = pendingUid;
    const seconds = pendingSeconds;
    pendingSeconds = 0;
    pendingUid = null;
    flushInFlight = true;

    try {
        const serverScore = await sendBatch(uid, seconds, keepalive);
        if (serverScore === null) {
            // Re-queue for the next flush window. Don't clobber a newer uid
            // that may have arrived while we were in-flight.
            if (pendingUid === null || pendingUid === uid) {
                pendingUid = uid;
                pendingSeconds += seconds;
            }
            return;
        }
        // Use max so we don't visibly drop points if more were added locally
        // during the in-flight request (those stay in the next pending batch).
        const today = todayKey();
        const stored = readStored();
        const localScore =
            stored && stored.uid === uid && stored.date === today ? stored.score : 0;
        const reconciled = Math.max(serverScore, localScore);
        writeStored({ uid, date: today, score: reconciled });
        dispatch(reconciled);
    } finally {
        flushInFlight = false;
        if (pendingSeconds > 0) scheduleFlush();
    }
};

export const flushPendingScore = (keepalive = false): Promise<void> => {
    if (flushTimer !== null) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
    return flushPending(keepalive);
};

export const refreshScoreFromServer = async (): Promise<number> => {
    const uid = currentUid();
    if (uid === null) return 0;
    // Drain any buffered local increments first so the server value we read
    // back already includes them.
    await flushPendingScore().catch(() => {});
    try {
        const res = await apiFetch(`/points/today?uid=${uid}`, {
            method: "GET",
        });
        if (!res.ok) return getSessionScore();
        const data = await res.json();
        const score = Number(data?.score);
        if (!Number.isFinite(score) || score < 0) return getSessionScore();
        writeStored({ uid, date: todayKey(), score });
        dispatch(score);
        return score;
    } catch {
        return getSessionScore();
    }
};

export const addSessionSeconds = (n: number): number => {
    if (!Number.isFinite(n) || n <= 0) return getSessionScore();
    const uid = currentUid();
    if (uid === null) return getSessionScore();
    const today = todayKey();
    const stored = readStored();
    const baseline =
        stored && stored.uid === uid && stored.date === today ? stored.score : 0;
    const rounded = Math.round(n);
    const optimistic = baseline + rounded;

    writeStored({ uid, date: today, score: optimistic });
    dispatch(optimistic);

    // If the buffered delta belongs to a different user, ship it under that
    // user before accepting this one — stops points bleeding across logins.
    if (pendingUid !== null && pendingUid !== uid) {
        void flushPending(false);
    }
    pendingUid = uid;
    pendingSeconds += rounded;
    scheduleFlush();

    return optimistic;
};

export const resetSessionScore = () => {
    try {
        window.localStorage.removeItem(DAILY_SCORE_KEY);
        pendingUid = null;
        pendingSeconds = 0;
        if (flushTimer !== null) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }
        dispatch(0);
    } catch {
        // noop
    }
};

// Flush on tab hide / unload so unsent points survive a close or background.
// keepalive lets the request complete during unload.
if (typeof window !== "undefined") {
    window.addEventListener("pagehide", () => {
        void flushPendingScore(true);
    });
    if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") {
                void flushPendingScore(true);
            }
        });
    }
}
